import clsx from 'clsx';
import { Heading, Text } from '@mysten/ui';
import { dgcToMist, mistToDgc, parseLimitPriceDgcPerData, quoteDgcMistForDataBid } from '@cpc/tx';
import { useMemo } from 'react';

import { useT } from '~/i18n/LocaleContext';
import { Card } from '~/ui/Card';

import {
    TRADE,
    TRADE_PRICE_DECIMALS,
    sanitizeLimitPriceInput,
    tryParseQtyLots,
} from './tradeTheme';
import { getClobLotSizeMist } from '~/config/clobConfig';
import { ShoppingBagIcon } from "@heroicons/react/24/outline";

const PCT_STEPS = [0, 25, 50, 75, 100] as const;

// 1. 输入框组件
function OrderField({
    label,
    value,
    onChange,
    unit,
    placeholder,
    maxDecimals = TRADE_PRICE_DECIMALS,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    unit?: string;
    placeholder?: string;
    maxDecimals?: number;
}) {
    return (
        <div className="mb-2 flex flex-col gap-1.5">
            <span className="text-caption font-medium text-[#aeb9d0]">
                {label}
                <span className="ml-1 text-[10px] font-normal text-[#758F9E]">
                    {`(最多 ${maxDecimals} 位小数)`}
                </span>
            </span>
            <div className="relative flex items-center rounded-lg bg-white/10 border border-white/10 px-3 py-2.5 focus-within:border-gray-700 transition">
                <input
                    className="w-full bg-transparent text-body font-semibold text-white placeholder-gray-600 outline-none tabular-nums"
                    type="text"
                    lang="en"
                    value={value}
                    onChange={(e) => onChange(sanitizeLimitPriceInput(e.target.value, maxDecimals))}
                    placeholder={placeholder}
                    inputMode="decimal"
                    enterKeyHint="done"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
                <button
                    type="button"
                    className="ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white/10 text-base font-bold text-white md:hidden"
                    aria-label="小数点"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                        if (value.includes('.')) return;
                        onChange(sanitizeLimitPriceInput(`${value}.`, maxDecimals));
                    }}
                >
                    .
                </button>
                {unit ? (
                    <span className="shrink-0 text-caption font-bold text-[#aeb9d0] ml-2 tabular-nums">
                        {unit}
                    </span>
                ) : null}
            </div>
        </div>
    );
}

// 2. 升级版滑块组件：支持任意位置点击和自由拖动
function PctSlider({
    pct,
    onPick,
    isBid
}: {
    pct: number;
    onPick: (p: number) => void;
    isBid: boolean
}) {
    const activeColor = isBid ? 'bg-[#10B981]' : 'bg-[#F97316]';
    const safePct = Math.min(100, Math.max(0, Math.round(pct)));

    return (
        <div className="mb-3 mt-1 px-1">
            {/* 进度条轨道容器 */}
            <div className="relative h-6 w-full flex items-center group select-none">
                
                {/* 视觉上的背景轨道 */}
                <div className="absolute left-0 right-0 h-1 rounded-full bg-[#2A2E39] pointer-events-none">
                    {/* 已选中的高亮进度 */}
                    <div
                        className={clsx("absolute left-0 top-0 h-full rounded-full", activeColor)}
                        style={{ width: `${safePct}%` }}
                    />
                </div>

                {/* 刻度小圆点 */}
                {PCT_STEPS.map((p) => {
                    const isActive = safePct >= p;
                    return (
                        <div
                            key={p}
                            className="absolute p-1 z-10 -translate-x-1/2 pointer-events-none"
                            style={{ left: `${p}%` }}
                        >
                            <span
                                className={clsx(
                                    'block h-2 w-2 rounded-full border transition-all duration-75',
                                    isActive
                                        ? isBid ? 'border-[#10B981] bg-[#10B981]' : 'border-[#F97316] bg-[#F97316]'
                                        : 'border-gray-500 bg-[#1E222D]'
                                )}
                            />
                        </div>
                    );
                })}

                {/* 隐藏的真实拖动输入源 */}
                <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={safePct}
                    onChange={(e) => onPick(Number(e.target.value))}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20 appearance-none m-0 p-0"
                    style={{ WebkitAppearance: 'none' }}
                />
            </div>

            {/* 进度条下方的 0% 到 100% 快捷快捷标签 */}
            <div className="relative w-full h-4 mt-1">
                {PCT_STEPS.map((p) => (
                    <button
                        key={`text-${p}`}
                        type="button"
                        onClick={() => onPick(p)}
                        className="absolute top-0 -translate-x-1/2 text-[10px] font-medium text-[#aeb9d0] hover:text-white transition-colors tabular-nums z-30 px-1"
                        style={{ left: `${p}%` }}
                    >
                        {p}%
                    </button>
                ))}
            </div>
        </div>
    );
}

function OrderSideColumn({
    side,
    price,
    onPriceChange,
    qty,
    onQtyChange,
    refPrice,
    cpcBal,
    dataBal,
    estimate,
    submitting,
    hasAccount,
    onSubmit,
    baseSymbol,
    quoteSymbol,
    lotSizeMist,
}: {
    side: 'bid' | 'ask';
    price: string;
    onPriceChange: (v: string) => void;
    qty: string;
    onQtyChange: (v: string) => void;
    refPrice: string;
    cpcBal: string;
    dataBal: string;
    estimate: string;
    submitting: boolean;
    hasAccount: boolean;
    onSubmit: () => void;
    baseSymbol: string;
    quoteSymbol: string;
    lotSizeMist: bigint;
}) {
    const t = useT();
    const isBid = side === 'bid';
    const bal = isBid ? dataBal : cpcBal;
    const balUnit = isBid ? quoteSymbol : baseSymbol;
    const sideLabel = String(isBid ? t('trade.buyDgc') : t('trade.sellDgc')).split('DGC').join(
        baseSymbol,
    );
    // Gas is always native DGC; only reserve from base when selling DGC itself.
    const reserveGasFromBase = !isBid && baseSymbol === 'DGC';

    // 计算当前数量占余额的百分比
    const pct = useMemo(() => {
        const q = tryParseQtyLots(qty, lotSizeMist);
        if (!q) return 0;

        try {
            if (isBid) {
                const currentPrice = price.trim() || refPrice;
                if (!currentPrice || currentPrice === '0') return 0;
                const ticks = parseLimitPriceDgcPerData(currentPrice);
                const need = quoteDgcMistForDataBid(ticks, q, lotSizeMist);
                const dataMist = dgcToMist(dataBal || '0');
                if (dataMist <= 0n) return 0;
                return Math.min(100, Number((need * 100n) / dataMist));
            }
            const totalMist = dgcToMist(cpcBal || '0');
            if (totalMist <= 0n) return 0;
            return Math.min(100, Number((q * lotSizeMist * 100n) / totalMist));
        } catch {
            return 0;
        }
    }, [qty, isBid, price, refPrice, cpcBal, dataBal, lotSizeMist]);

    // 点击/拖动进度条时计算出对应数量
    const applyPct = (p: number) => {
        try {
            if (isBid) {
                const dataMist = dgcToMist(dataBal || '0');
                const spend = (dataMist * BigInt(p)) / 100n;
                const currentPrice = price.trim() || refPrice;
                const ticks = parseLimitPriceDgcPerData(currentPrice);
                const denom = ticks * lotSizeMist;
                if (denom <= 0n) return;
                const qtyLots = (spend * 1_000_000_000n) / denom;
                onQtyChange(mistToDgc(qtyLots * lotSizeMist));
                return;
            }
            const totalMist = dgcToMist(cpcBal || '0');
            const reserve = reserveGasFromBase ? 10_000_000n : 0n;
            const sellable = totalMist > reserve ? totalMist - reserve : 0n;
            const targetMist = (sellable * BigInt(p)) / 100n;
            const aligned = (targetMist / lotSizeMist) * lotSizeMist;
            onQtyChange(mistToDgc(aligned));
        } catch {
            /* ignore */
        }
    };

    return (
        <div className="min-w-0 flex flex-col">
            <Heading
                variant="heading5/semibold"
                className={clsx("mb-4 text-xl", isBid ? 'text-[#10B981]' : 'text-[#F97316]')}
            >
                {sideLabel}
            </Heading>

            <OrderField
                label={t('trade.limitPrice')}
                value={price}
                onChange={onPriceChange}
                placeholder={refPrice}
                unit={quoteSymbol}
            />

            <OrderField
                label={t('trade.quantity')}
                value={qty}
                onChange={onQtyChange}
                unit={baseSymbol}
                placeholder="0"
                maxDecimals={9}
            />

            <PctSlider pct={pct} onPick={applyPct} isBid={isBid} />

            <div className="mb-2 mt-2 flex items-center justify-between text-caption font-medium">
                <span className="text-[#aeb9d0]">{t('common.available')}</span>
                <span className="text-white font-semibold tabular-nums">
                    {bal || '0'} {balUnit}
                </span>
            </div>

            <div className="mb-5 flex items-center justify-between text-caption font-medium">
                <span className="text-[#aeb9d0]">
                    {isBid ? t('trade.estimateSpend') : t('trade.estimateReceive')}
                </span>
                <span className={clsx("font-bold tabular-nums", isBid ? 'text-[#10B981]' : 'text-[#F97316]')}>
                    {estimate || '0.00'}
                </span>
            </div>

            <button
                type="button"
                className={clsx(
                    'w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white shadow-lg transition active:scale-[0.99] disabled:opacity-40 disabled:pointer-events-none',
                    isBid
                        ? 'bg-[#10B981] hover:bg-[#059669] shadow-[#10B981]/10'
                        : 'bg-[#F97316] hover:bg-[#EA580C] shadow-[#F97316]/10'
                )}
                disabled={submitting || !hasAccount}
                onClick={onSubmit}
            >
                {!hasAccount ? (
                    t('common.connectWallet')
                ) : submitting ? (
                    t('common.submitting')
                ) : (
                    <>
                        {isBid ? (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-[#10B981] font-extrabold text-sm">＋</span>
                        ) : (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-[#F97316] font-extrabold text-sm">－</span>
                        )}
                        <span>{sideLabel}</span>
                    </>
                )}
            </button>
        </div>
    );
}

export function TradeOrderForm({
    bidPrice,
    askPrice,
    onBidPriceChange,
    onAskPriceChange,
    bidQty,
    askQty,
    onBidQtyChange,
    onAskQtyChange,
    bidEstimate,
    askEstimate,
    refPrice,
    cpcBal,
    dataBal,
    submitting,
    hasAccount,
    onSubmitBid,
    onSubmitAsk,
    baseSymbol = 'DGC',
    quoteSymbol = 'DATA',
    lotSizeMist = getClobLotSizeMist(),
}: {
    bidPrice: string;
    askPrice: string;
    onBidPriceChange: (v: string) => void;
    onAskPriceChange: (v: string) => void;
    bidQty: string;
    askQty: string;
    onBidQtyChange: (v: string) => void;
    onAskQtyChange: (v: string) => void;
    bidEstimate: string;
    askEstimate: string;
    refPrice: string;
    cpcBal: string;
    dataBal: string;
    submitting: boolean;
    hasAccount: boolean;
    onSubmitBid: () => void;
    onSubmitAsk: () => void;
    baseSymbol?: string;
    quoteSymbol?: string;
    lotSizeMist?: bigint;
}) {
    return (
        <Card bg="dark" spacing="sm" growOnHover>
            <div className="mb-2 flex items-center gap-2 border-b border-[#aeb9d0]/60 pb-1.5">
                <ShoppingBagIcon className="h-5 w-5 text-[#aeb9d0]" />
                <Heading variant="heading4/semibold" color="dark" className="text-lg font-bold">
                    Place Order
                </Heading>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:border-r md:border-[#aeb9d0]/60 md:pr-4">
                    <OrderSideColumn
                        side="bid"
                        price={bidPrice}
                        onPriceChange={onBidPriceChange}
                        qty={bidQty}
                        onQtyChange={onBidQtyChange}
                        refPrice={refPrice}
                        cpcBal={cpcBal}
                        dataBal={dataBal}
                        estimate={bidEstimate}
                        submitting={submitting}
                        hasAccount={hasAccount}
                        onSubmit={onSubmitBid}
                        baseSymbol={baseSymbol}
                        quoteSymbol={quoteSymbol}
                        lotSizeMist={lotSizeMist}
                    />
                </div>
                <div className="md:pl-4">
                    <OrderSideColumn
                        side="ask"
                        price={askPrice}
                        onPriceChange={onAskPriceChange}
                        qty={askQty}
                        onQtyChange={onAskQtyChange}
                        refPrice={refPrice}
                        cpcBal={cpcBal}
                        dataBal={dataBal}
                        estimate={askEstimate}
                        submitting={submitting}
                        hasAccount={hasAccount}
                        onSubmit={onSubmitAsk}
                        baseSymbol={baseSymbol}
                        quoteSymbol={quoteSymbol}
                        lotSizeMist={lotSizeMist}
                    />
                </div>
            </div>
        </Card>
    );
}

export { TRADE };